import MenuItem from "@mui/material/MenuItem/MenuItem";
import Select from "@mui/material/Select/Select";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Category } from "../../../api/models/Category";
import LeadForm from "./LeadDetails/LeadForm";
import "./Leads.scss";
import {
  AppButton,
  AppDialog,
  FormField,
  FormSection,
  PageHeader,
} from "../../../components/ui";
import {
  LeadType,
  CategoryMapping,
  ReasonForNAEmpDisplayMapping,
  ReasonForPIEmpDisplayMapping,
} from "./shared/Constants";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { HelpersService } from "../../../services/Helpers";
import {
  AdminLeadStatus,
  ArchiveRequest,
  ClientSearchRequest,
  HubspotService,
  LeadsService,
  ReasonForNAEmp,
  ReasonForNAPI,
} from "../../../api";
import EmploymentForm from "./LeadDetails/EmploymentForm";
import PersonalInjuryForm from "./LeadDetails/PersonalInjuryForm";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  startSpinner,
  stopSpinner,
} from "../../../redux/actions/spinnerActions";
import WorkersCompForm from "./LeadDetails/WorkersCompForm";

const MySwal = withReactContent(Swal);

const JLG_INTAKE_URL = (process.env.REACT_APP_JLG_INTAKE_URL || "")
  .trim()
  .replace(/\/$/, "");

const CATEGORY_ICONS: Record<string, ReactNode> = {
  EMPLOYMENT: <WorkOutlineIcon fontSize="small" />,
  PERSONAL_INJURY: <PersonOutlineIcon fontSize="small" />,
  WORKERS_COMPENSATION: <HealthAndSafetyOutlinedIcon fontSize="small" />,
  CLASS_ACTION: <GroupsOutlinedIcon fontSize="small" />,
};

export default function AddLead() {
  const jlgIntakeUrl = JLG_INTAKE_URL;
  const [displayedCategories] = useState<string[]>([
    HelpersService.getEnumKeyByEnumValue(Category, Category.EMPLOYMENT),
    HelpersService.getEnumKeyByEnumValue(Category, Category.PERSONAL_INJURY),
    HelpersService.getEnumKeyByEnumValue(
      Category,
      Category.WORKERS_COMPENSATION
    ),
    HelpersService.getEnumKeyByEnumValue(Category, Category.CLASS_ACTION),
  ]);

  const [selectedCategory, setSelectedCategory] = useState<Category>(
    HelpersService.getEnumKeyByEnumValue(Category, Category.EMPLOYMENT)
  );
  const [email, setEmail] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [openNADialog, setOpenNADialog] = useState(false);

  const [selectedEmpReason, setSelectedEmpReason] = useState<ReasonForNAEmp>();
  const [selectedPiReason, setSelectedPiReason] = useState<ReasonForNAPI>();
  const [emailFound, setEmailFound] = useState<boolean>(false);

  const user = useSelector((state: any) => state.user?.user);
  const dispatch = useDispatch();

  const supportsSetAsNa =
    Category[selectedCategory] === Category.EMPLOYMENT ||
    Category[selectedCategory] === Category.PERSONAL_INJURY;

  useEffect(() => {
    setSelectedCategory(
      HelpersService.getEnumKeyByEnumValue(Category, Category.EMPLOYMENT)
    );
  }, [user]);

  const hubspotAlertSeverity = useMemo(() => {
    if (!errorMessage) return undefined;
    if (errorMessage.toLowerCase().includes("already")) return "warning";
    if (errorMessage.toLowerCase().includes("no contact")) return "info";
    return "warning";
  }, [errorMessage]);

  const handleCategoryChange = (nextCategory: string) => {
    if (!nextCategory || nextCategory === selectedCategory) return;

    MySwal.fire({
      title: <strong>Confirm category change</strong>,
      html: (
        <div>
          Changing category will delete all data that you added so far.
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, change",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedCategory(nextCategory as Category);
        setSelectedEmpReason(undefined);
        setSelectedPiReason(undefined);
        setOpenNADialog(false);
      }
    });
  };

  const handleLookHubspot = () => {
    setErrorMessage("");
    if (!email) {
      MySwal.fire({
        icon: "error",
        title: <strong>Please provide a valid email</strong>,
      }).then();
      return;
    }
    dispatch(startSpinner());
    HubspotService.postApiHubspotSearchClientInHubspot({
      email: email,
    } as ClientSearchRequest)
      .then((res) => {
        setEmailFound(!!res);
        if (!res) {
          setErrorMessage(
            "No contact found with this email. Submitting a new lead will create a new contact on Hubspot."
          );
          return;
        }
        if (res.alreadyHasDeal) {
          setErrorMessage(
            "There is already a deal associated with this client on Hubspot. Please make sure you need a new deal."
          );
        }

        switch (Category[selectedCategory]) {
          case Category.EMPLOYMENT: {
            EmploymentForm.updateUserData(res);
            break;
          }
          case Category.WORKERS_COMPENSATION: {
            WorkersCompForm.updateUserData(res);
            break;
          }
          case Category.PERSONAL_INJURY: {
            PersonalInjuryForm.updateUserData(res);
            break;
          }
        }
      })
      .catch(() => {
        setEmailFound(false);
        MySwal.fire({
          icon: "error",
          title: <strong>HubSpot search failed</strong>,
          text: "Could not search HubSpot for this email. Please try again or re-authorize HubSpot.",
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const handleOpenNaDialog = () => {
    if (!supportsSetAsNa) return;
    if (!emailFound) {
      MySwal.fire({
        icon: "error",
        title: <strong>Please find a valid user in hubspot first</strong>,
      }).then();
      return;
    }

    setOpenNADialog(true);
  };

  const handleNADialogClose = () => {
    let updateRequest = {
      status: AdminLeadStatus.N_A,
      clientEmail: email,
      reasonForNAEmp: selectedEmpReason
        ? ReasonForNAEmp[selectedEmpReason]
        : undefined,
      reasonForNAPI: selectedPiReason
        ? ReasonForNAPI[selectedPiReason]
        : undefined,
    } as ArchiveRequest;

    dispatch(startSpinner());
    LeadsService.postApiLeadsSetAsNa(updateRequest)
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: <p className="title">Client updated!</p>,
        }).then(() => {});
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error updating client</p>,
        });
      })
      .finally(() => {
        dispatch(stopSpinner());
        setOpenNADialog(false);
      });
  };

  return (
    <div className="new-lead-holder">
      <PageHeader
        title="New Intake"
        subtitle="Capture initial client information to get started."
        actions={
          jlgIntakeUrl ? (
            <AppButton
              variant="outlined"
              color="primary"
              startIcon={<OpenInNewIcon />}
              onClick={() =>
                window.open(jlgIntakeUrl, "_blank", "noopener,noreferrer")
              }
            >
              Open JLG Intake
            </AppButton>
          ) : undefined
        }
      />

      <Box className="intake-page">
        <FormSection title="Practice area" className="intake-setup-section">
          <FormControl
            size="small"
            className="intake-practice-select"
            sx={{ maxWidth: 360 }}
            fullWidth
          >
            <InputLabel id="practice-area-label">Practice area</InputLabel>
            <Select
              labelId="practice-area-label"
              label="Practice area"
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(String(e.target.value))}
            >
              {displayedCategories.map((key) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {CATEGORY_ICONS[key]}
                    {CategoryMapping[Category[key]] ??
                      HelpersService.camelToString(Category[key])}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormSection>

        <Box className="intake-main">
          <Box className="intake-form-body">
            <LeadForm
              selectedCategory={selectedCategory}
              isEdit={true}
              isNew={true}
              leadType={LeadType.Admin}
              clientLookup={
                <>
                  <Box className="row g-2 align-items-end intake-lookup mb-2">
                    <div className="col-12 col-md-6">
                      <FormField
                        required
                        label="Client email"
                        type="email"
                        size="small"
                        autoComplete="email"
                        placeholder="Enter client email address"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value.trim());
                          setEmailFound(false);
                          setErrorMessage(undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleLookHubspot();
                          }
                        }}
                        fullWidth
                        InputProps={{
                          endAdornment: emailFound ? (
                            <InputAdornment position="end">
                              <CheckCircleOutlineIcon
                                color="success"
                                fontSize="small"
                                aria-label="Client found"
                              />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <AppButton
                        className="intake-lookup__btn w-100"
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleLookHubspot}
                        disabled={!email}
                      >
                        Look for Client in Hubspot
                      </AppButton>
                    </div>
                    {supportsSetAsNa && (
                      <div className="col-12 col-md-3">
                        <AppButton
                          className="intake-lookup__btn w-100"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenNaDialog()}
                          disabled={!emailFound}
                        >
                          Set as N/A
                        </AppButton>
                      </div>
                    )}
                  </Box>
                  {hubspotAlertSeverity && errorMessage && (
                    <Alert
                      className="intake-lookup__alert"
                      severity={hubspotAlertSeverity}
                      variant="outlined"
                    >
                      {errorMessage}
                    </Alert>
                  )}
                </>
              }
            />
          </Box>
        </Box>
      </Box>

      <AppDialog
        open={openNADialog}
        onClose={() => setOpenNADialog(false)}
        title="Set as N/A"
        actions={
          <>
            <AppButton
              color="gray"
              variant="contained"
              onClick={() => setOpenNADialog(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!selectedEmpReason && !selectedPiReason}
              color="primary"
              variant="contained"
              onClick={() => handleNADialogClose()}
            >
              Confirm
            </AppButton>
          </>
        }
      >
        {Category[selectedCategory] === Category.EMPLOYMENT && (
          <FormControl fullWidth>
            <InputLabel id="selected-label">Select reason*</InputLabel>
            <Select
              labelId="selected-label"
              label="Select reason"
              value={selectedEmpReason ?? ""}
              onChange={(e) =>
                setSelectedEmpReason(e.target.value as ReasonForNAEmp)
              }
            >
              {Object.keys(ReasonForNAEmp).map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {ReasonForNAEmpDisplayMapping[ReasonForNAEmp[reason]]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {Category[selectedCategory] === Category.PERSONAL_INJURY && (
          <FormControl fullWidth>
            <InputLabel id="selected-label">Select reason*</InputLabel>
            <Select
              labelId="selected-label"
              label="Select reason"
              value={selectedPiReason ?? ""}
              onChange={(e) =>
                setSelectedPiReason(e.target.value as ReasonForNAPI)
              }
            >
              {Object.keys(ReasonForNAPI).map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {ReasonForPIEmpDisplayMapping[ReasonForNAPI[reason]]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </AppDialog>
    </div>
  );
}
